import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import type { RfqListItemDto } from '../../lib/rfqApi';
import SegmentedControl, { type Segment } from '../../components/ui/SegmentedControl';
import Button from '../../components/ui/Button';
import { FOCUS } from '../../components/ui/tokens';
import LifecycleTrail, { TONE_DOT } from './LifecycleTrail';
import LifecycleLegend from './LifecycleLegend';
import type { RfqLifecycles } from './useRfqLifecycles';
import { STAGE_SPECS, buildTrail, rowState, type RowState, type TrailBox } from './lifecycleModel';

type StateFilter = 'all' | RowState;

const PAGE_SIZE = 50;

// A row says "In progress" or "Completed". "Needs attention" is not drawn on the row (D150): the amber dot on a
// box and the Needs attention filter above the list say it.
const STATE_TAG: Partial<Record<RowState, { label: string; className: string }>> = {
  open: { label: 'In progress', className: 'bg-sky-50 text-sky-800 border-sky-200' },
  completed: { label: 'Completed', className: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};


interface MenuState {
  rowId: string;
  box: TrailBox;
  /** Below the box: its top edge. Above it (no room below): the distance from the screen's bottom edge. */
  top?: number;
  bottom?: number;
  left: number;
  /** The list scrolls inside the menu when it is taller than this. */
  maxHeight: number;
}

const MENU_GAP = 6;
const MENU_EDGE = 12;
const MENU_HEADER_PX = 32;
const MENU_ROW_PX = 52;
const MENU_MAX_PX = 420;
const MENU_MIN_PX = 140;

/**
 * Where a list of records opens under (or over) its box, and how tall it may be. It opens downward when
 * the whole list fits there, else on whichever side has more room, and its list scrolls inside itself
 * when even that is too short: ten POs on a row near the bottom of the screen are all reachable.
 */
export interface MenuPlacement {
  top?: number;
  bottom?: number;
  left: number;
  maxHeight: number;
}

export function placeMenu(box: DOMRect, count: number, viewport: { width: number; height: number }): MenuPlacement {
  const wanted = Math.min(MENU_HEADER_PX + count * MENU_ROW_PX + MENU_EDGE, MENU_MAX_PX);
  const below = viewport.height - box.bottom - MENU_GAP - MENU_EDGE;
  const above = box.top - MENU_GAP - MENU_EDGE;
  const up = below < wanted && above > below;
  const room = up ? above : below;
  return {
    maxHeight: Math.max(MENU_MIN_PX, Math.min(wanted, room)),
    left: Math.max(8, Math.min(box.left, viewport.width - 300)),
    ...(up ? { bottom: viewport.height - box.top + MENU_GAP } : { top: box.bottom + MENU_GAP }),
  };
}

export interface RfqLifecycleListProps {
  /** The rows to show, already filtered and sorted by the board. */
  rfqs: RfqListItemDto[];
  lifecycles: RfqLifecycles;
  /** The client as the board names it (strips the email address off "Name <a@b.c>"). */
  clientName: (rfq: RfqListItemDto) => string;
}

/**
 * Home, lifecycle view: one trail per RFQ, line by line, headed by the vessel.
 *
 * Opening a document is a plain navigation to that document's own page, so nothing about those pages
 * changes. A box holding several documents (three vendor POs) opens a small list under it first.
 */
const RfqLifecycleList: React.FC<RfqLifecycleListProps> = ({ rfqs, lifecycles, clientName }) => {
  const navigate = useNavigate();
  const quotations = usePermission('quotations');
  const confirmedOrders = usePermission('confirmed_orders');
  const purchaseOrders = usePermission('vendor_purchase_orders');
  const invoices = usePermission('invoices');

  const canViewScreen = useMemo(
    () => ({
      quotations: quotations.canView,
      confirmed_orders: confirmedOrders.canView,
      vendor_purchase_orders: purchaseOrders.canView,
      invoices: invoices.canView,
    }),
    [quotations.canView, confirmedOrders.canView, purchaseOrders.canView, invoices.canView]
  );
  const canOpen = useCallback(
    (box: TrailBox) => {
      const spec = STAGE_SPECS.find((s) => s.key === box.key);
      return !!spec && canViewScreen[spec.linkScreen];
    },
    [canViewScreen]
  );

  const [filter, setFilter] = useState<StateFilter>('all');
  const [shown, setShown] = useState(PAGE_SIZE);
  const [menu, setMenu] = useState<MenuState | null>(null);

  // One row's state, once per row per load. Drawn from the same lifecycle the boxes are.
  const rows = useMemo(
    () =>
      rfqs.map((rfq) => {
        const lifecycle = lifecycles.forRfq(rfq._id);
        const state: RowState | null = typeof lifecycle === 'object' ? rowState(lifecycle) : null;
        return { rfq, lifecycle, state, boxes: buildTrail(rfq, lifecycle) };
      }),
    [rfqs, lifecycles]
  );

  const counts = useMemo(() => {
    const c = { all: rows.length, attention: 0, open: 0, completed: 0 };
    rows.forEach((r) => {
      if (r.state) c[r.state] += 1;
    });
    return c;
  }, [rows]);

  // The status filters need every row's lifecycle, so they appear once it has loaded (never as zeroes).
  const segments: Segment<StateFilter>[] = [
    { value: 'all', label: 'All RFQs', count: counts.all },
    ...(lifecycles.status === 'ready'
      ? ([
          { value: 'attention', label: 'Needs attention', count: counts.attention },
          { value: 'open', label: 'In progress', count: counts.open },
          { value: 'completed', label: 'Completed', count: counts.completed },
        ] as Segment<StateFilter>[])
      : []),
  ];

  const filtered = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.state === filter)),
    [rows, filter]
  );
  const visible = filtered.slice(0, shown);

  // A new filter or a new set of rows starts from the top of the list again.
  useEffect(() => setShown(PAGE_SIZE), [filter, rfqs]);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    // The list scrolls inside the menu: that must not close it. Any other scroll moves the box away from it.
    const onScroll = (e: Event) => {
      const target = e.target as Element | null;
      if (target && typeof target.closest === 'function' && target.closest('[role="menu"]')) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // A press anywhere but the menu or a box that opens one closes it. A box that opens one is left to its
    // own click, which toggles it, so pressing the open box does not close and instantly reopen it.
    const onPress = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target && target.closest('[role="menu"], [aria-haspopup="menu"]')) return;
      close();
    };
    // `true` so a scroll of the list (not only the window) closes it: the menu is positioned once.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPress);
    return () => {
      window.removeEventListener('mousedown', onPress);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const select = (rowId: string, box: TrailBox, anchor: HTMLElement) => {
    if (box.docs.length === 1) {
      setMenu(null);
      navigate(box.docs[0].to);
      return;
    }
    if (menu && menu.rowId === rowId && menu.box.key === box.key) {
      setMenu(null);
      return;
    }
    setMenu({
      rowId,
      box,
      ...placeMenu(anchor.getBoundingClientRect(), box.docs.length, { width: window.innerWidth, height: window.innerHeight }),
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SegmentedControl<StateFilter> aria-label="Lifecycle status" value={filter} onChange={setFilter} segments={segments} />
        {lifecycles.status === 'error' ? (
          <div role="alert" className="flex items-center gap-2 text-xs text-red-700">
            The lifecycle could not be loaded.
            <Button size="sm" onClick={lifecycles.retry}>
              Retry
            </Button>
          </div>
        ) : null}
      </div>

      {rows.length > 0 ? <LifecycleLegend /> : null}

      {visible.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">
          {rows.length === 0 ? 'No RFQs match.' : 'No RFQs in this status.'}
        </div>
      ) : (
        visible.map(({ rfq, state, boxes }) => {
          const vessel = (rfq.vesselName || '').trim();
          const tag = state ? STATE_TAG[state] : undefined;
          return (
            <section key={rfq._id} aria-label={`RFQ ${rfq.rfqId || rfq._id}`}>
              <div className="mb-1.5 flex items-baseline gap-2 px-0.5">
                <h3 className="border-l-4 border-primary-500 pl-2 text-sm font-semibold leading-tight text-slate-900">
                  {vessel || <span className="font-normal text-slate-400">Vessel not named</span>}
                </h3>
                <span className="truncate text-xs text-slate-500">{clientName(rfq)}</span>
                {tag ? (
                  <span className={`ml-auto shrink-0 rounded-full border px-2 text-xs font-semibold ${tag.className}`}>{tag.label}</span>
                ) : null}
              </div>
              <LifecycleTrail
                boxes={boxes}
                canOpen={canOpen}
                openKey={menu && menu.rowId === rfq._id ? menu.box.key : null}
                onSelect={(box, anchor) => select(rfq._id, box, anchor)}
                label={`Lifecycle of ${rfq.rfqId || rfq._id}`}
              />
            </section>
          );
        })
      )}

      {filtered.length > shown ? (
        <div className="flex justify-center">
          <Button size="sm" onClick={() => setShown((n) => n + PAGE_SIZE)}>
            Show more ({filtered.length - shown} left)
          </Button>
        </div>
      ) : null}

      {menu ? (
        <div
          role="menu"
          aria-label={`${menu.box.label} records`}
          style={{ top: menu.top, bottom: menu.bottom, left: menu.left, maxHeight: menu.maxHeight }}
          className="fixed z-50 flex w-72 flex-col rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg"
          // A click inside must not reach the board's own click-away handlers.
          onClick={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 px-2 pb-1 pt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {menu.box.label} ({menu.box.docs.length})
          </div>
          <div className="min-h-0 overflow-y-auto overscroll-contain">
          {menu.box.docs.map((doc) => (
            <button
              key={doc.id}
              type="button"
              role="menuitem"
              onClick={() => {
                setMenu(null);
                navigate(doc.to);
              }}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-primary-50 ${FOCUS}`}
            >
              <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${TONE_DOT[doc.tone]}`} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-900">{doc.title}</span>
                {doc.detail ? <span className="block truncate text-xs text-slate-500">{doc.detail}</span> : null}
              </span>
            </button>
          ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RfqLifecycleList;
